import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Loader2, AlertCircle, Monitor, Smartphone } from 'lucide-react';
import { notificationService } from '@/services/notification.service';
import Pagination from '@/components/ui/Pagination';

// Get initials for avatar fallback
const getInitials = (name?: string): string => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export function PushSubscribers() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  // Track avatar URLs that 404'd so we fall back to initials instead of a
  // broken-image icon
  const [brokenAvatars, setBrokenAvatars] = useState<Set<string>>(new Set());

  const offset = (currentPage - 1) * itemsPerPage;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['push-subscribers', currentPage, itemsPerPage],
    queryFn: () => notificationService.getPushSubscribers({ limit: itemsPerPage, offset }),
    // Don't burn OneSignal API calls on every tab switch — admins hop around
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const subscribers = data?.subscribers || [];
  const totalCount = data?.total_count ?? subscribers.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleItemsPerPageChange = (perPage: number) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-primary-600" />
        <h2 className="text-lg font-semibold text-gray-900">Push Subscribers</h2>
        <span className="text-xs text-gray-500">(live from OneSignal)</span>
      </div>

      {/* Card */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span className="text-sm">Loading subscribers...</span>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <AlertCircle className="w-12 h-12 mb-4 text-red-400" />
            <p className="font-semibold text-lg text-gray-900">Failed to load subscribers</p>
            <p className="text-sm mt-1 max-w-md text-center">
              {(error as Error)?.message || 'Unable to reach OneSignal. Check tenant configuration.'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        ) : subscribers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <Users className="w-16 h-16 mb-4 opacity-30" />
            <p className="font-semibold text-lg">No subscribers found</p>
            <p className="text-sm mt-1">Subscribed users will appear here once they opt in.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Employee ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscribers.map((sub) => {
                  const subscription = sub.subscriptions?.[0];
                  const deviceType = subscription?.type || 'Unknown';
                  const isEnabled = subscription?.enabled ?? false;
                  const platform = subscription?.platform;
                  const deviceOs = subscription?.device_os;
                  // "Mobile" platforms get a phone icon; everything else (desktop
                  // browsers, native Windows/macOS apps) gets the monitor icon.
                  const isMobile = platform === 'Android' || platform === 'iOS' || platform === 'iPadOS';
                  const PlatformIcon = isMobile ? Smartphone : Monitor;
                  // Subline: "Windows 10", "Android 13", or just the platform
                  // when device_os is missing/redundant.
                  const platformLine = platform && deviceOs && !deviceOs.toLowerCase().includes(platform.toLowerCase())
                    ? `${platform} ${deviceOs}`
                    : (deviceOs || platform || '');

                  return (
                    <tr
                      key={sub.onesignal_id || sub.external_id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {/* User cell — avatar + name + email, or — for anonymous */}
                      <td className="px-4 py-4">
                        {sub.user_name || sub.user_email ? (
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-indigo-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {sub.user_profile_image && !brokenAvatars.has(sub.user_profile_image) ? (
                                <img
                                  src={sub.user_profile_image}
                                  alt={sub.user_name || sub.user_email || ''}
                                  className="w-full h-full object-cover"
                                  onError={() =>
                                    setBrokenAvatars((prev) => {
                                      const next = new Set(prev);
                                      next.add(sub.user_profile_image!);
                                      return next;
                                    })
                                  }
                                />
                              ) : (
                                <span className="text-white font-semibold text-sm">
                                  {getInitials(sub.user_name || sub.user_email)}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {sub.user_name || '—'}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {sub.user_email || sub.external_id || '—'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* Employee ID */}
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {sub.user_employee_id || <span className="text-gray-400">—</span>}
                      </td>

                      {/* Device — browser/type with platform + OS subline */}
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2.5">
                          <PlatformIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-gray-900 truncate">{deviceType}</p>
                            {platformLine && (
                              <p className="text-xs text-gray-500 truncate">{platformLine}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Status badge */}
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            isEnabled
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              isEnabled ? 'bg-green-500' : 'bg-gray-400'
                            }`}
                          />
                          {isEnabled ? 'Subscribed' : 'Disabled'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination — render whenever there's at least one subscriber so the
            items-per-page selector is always available, even on single-page lists.
            totalCount comes from the live (filtered) count returned by the SDK,
            so the page math reflects only currently-subscribed sessions. */}
        {!isError && !isLoading && subscribers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      <p className="text-xs text-gray-500">
        Subscriber list is queried live from OneSignal. Inactive users are auto-unsubscribed
        when an admin sends a notification — no manual action needed.
      </p>
    </div>
  );
}
